
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';

export interface CommunityFeature {
  id: string;
  feature_key: string;
  name: string;
  description: string;
  community_id: string;
  created_at: string;
  updated_at: string;
}

export interface PlanFeature extends CommunityFeature {
  plan_feature_id: string;
  plan_feature_created_at: string;
  is_inherited?: boolean;
  inherited_from_plan?: string;
  inherited_from_rank?: number;
}

export interface PlanWithFeatures {
  id: string;
  name: string;
  rank: number;
  direct_features: PlanFeature[];
  inherited_features: PlanFeature[];
}

// Hook to get all features for a community
export function useCommunityFeatures(communityId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['community-features', communityId],
    queryFn: async () => {
      if (!communityId) {
        throw new Error('Community ID is required');
      }

      const { data, error } = await supabase
        .from('community_subscription_features')
        .select('*')
        .eq('community_id', communityId)
        .order('name');

      if (error) {
        throw error;
      }

      return data as CommunityFeature[];
    },
    enabled: !!user && !!communityId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// Hook to get features for a specific plan
export function usePlanFeatures(planId?: string, communityId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['plan-features', planId, communityId],
    queryFn: async () => {
      if (!planId || !communityId) {
        throw new Error('Plan ID and Community ID are required');
      }

      // Get current plan details
      const { data: currentPlan, error: planError } = await supabase
        .from('community_subscription_plans')
        .select('rank, name')
        .eq('id', planId)
        .single();

      if (planError) {
        throw planError;
      }

      // Get direct features for this plan using !inner join
      const { data: directFeatures, error: directError } = await supabase
        .from('community_plan_features')
        .select(`
          id,
          created_at,
          community_subscription_features!inner (
            id,
            feature_key,
            name,
            description,
            community_id,
            created_at,
            updated_at
          )
        `)
        .eq('plan_id', planId);

      if (directError) {
        throw directError;
      }

      // Transform direct features
      const transformedDirectFeatures = directFeatures?.map(pf => ({
        ...pf.community_subscription_features,
        plan_feature_id: pf.id,
        plan_feature_created_at: pf.created_at,
        is_inherited: false
      })) || [];

      // Get lower-ranked plan names for inheritance display
      const { data: lowerPlans, error: lowerPlansError } = await supabase
        .from('community_subscription_plans')
        .select('name, rank')
        .eq('community_id', communityId)
        .lt('rank', currentPlan.rank)
        .eq('is_active', true)
        .order('rank');

      if (lowerPlansError) {
        throw lowerPlansError;
      }

      return {
        directFeatures: transformedDirectFeatures,
        inheritedFromPlans: lowerPlans?.map(p => p.name) || []
      };
    },
    enabled: !!user && !!planId && !!communityId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

// Hook to get plan hierarchy with features
export function usePlanHierarchy(communityId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['plan-hierarchy', communityId],
    queryFn: async () => {
      if (!communityId) {
        throw new Error('Community ID is required');
      }

      // Get all plans for the community
      const { data: plans, error: plansError } = await supabase
        .from('community_subscription_plans')
        .select('*')
        .eq('community_id', communityId)
        .eq('is_active', true)
        .order('rank');

      if (plansError) {
        throw plansError;
      }

      // For each plan, get its features with inheritance
      const plansWithFeatures = await Promise.all(plans.map(async (plan) => {
        // Get direct features
        const { data: directFeatures } = await supabase
          .from('community_plan_features')
          .select(`
            id,
            created_at,
            community_subscription_features (
              id,
              feature_key,
              name,
              description,
              community_id,
              created_at,
              updated_at
            )
          `)
          .eq('plan_id', plan.id);

        // Get inherited features from lower-ranked plans
        const { data: inheritedFeatures } = await supabase
          .from('community_plan_features')
          .select(`
            id,
            created_at,
            community_subscription_features (
              id,
              feature_key,
              name,
              description,
              community_id,
              created_at,
              updated_at
            ),
            community_subscription_plans!inner (
              name,
              rank
            )
          `)
          .eq('community_subscription_plans.community_id', communityId)
          .lt('community_subscription_plans.rank', plan.rank)
          .eq('community_subscription_plans.is_active', true);

        const transformedDirectFeatures = directFeatures?.map(pf => ({
          ...pf.community_subscription_features,
          plan_feature_id: pf.id,
          plan_feature_created_at: pf.created_at,
          is_inherited: false
        })) || [];

        const directFeatureKeys = new Set(transformedDirectFeatures.map(f => f.feature_key));
        const transformedInheritedFeatures = inheritedFeatures?.filter(pf => 
          !directFeatureKeys.has(pf.community_subscription_features.feature_key)
        ).map(pf => ({
          ...pf.community_subscription_features,
          plan_feature_id: pf.id,
          plan_feature_created_at: pf.created_at,
          is_inherited: true,
          inherited_from_plan: pf.community_subscription_plans.name,
          inherited_from_rank: pf.community_subscription_plans.rank
        })) || [];

        return {
          id: plan.id,
          name: plan.name,
          rank: plan.rank,
          direct_features: transformedDirectFeatures,
          inherited_features: transformedInheritedFeatures
        };
      }));

      return plansWithFeatures as PlanWithFeatures[];
    },
    enabled: !!user && !!communityId,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to manage plan features (add/remove)
export function useManagePlanFeatures() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      planId, 
      featureIds, 
      communityId 
    }: { 
      planId: string; 
      featureIds: string[]; 
      communityId: string; 
    }) => {
      // Remove all existing features for this plan
      const { error: deleteError } = await supabase
        .from('community_plan_features')
        .delete()
        .eq('plan_id', planId);

      if (deleteError) {
        throw new Error(`Failed to remove existing features: ${deleteError.message}`);
      }

      // Add new features if any
      if (featureIds.length > 0) {
        const featureInserts = featureIds.map(featureId => ({
          plan_id: planId,
          feature_id: featureId
        }));

        const { error: insertError } = await supabase
          .from('community_plan_features')
          .insert(featureInserts);

        if (insertError) {
          throw new Error(`Failed to add features: ${insertError.message}`);
        }
      }

      return { success: true };
    },
    onSuccess: (_, { planId, communityId }) => {
      // Invalidate and refetch plan features and hierarchy
      queryClient.invalidateQueries({ 
        queryKey: ['plan-features', planId, communityId] 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['plan-hierarchy', communityId] 
      });
      
      toast({
        title: 'Success',
        description: 'Plan features updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to update plan features. Please try again.',
        variant: 'destructive',
      });
    },
  });
}
